import React, { useState, useEffect } from 'react';
import { calendarAPI } from '../../services/api';
import { GroupedHypoEvent, GroupedHyperEvent } from '../../types';

interface DayData {
  date: string;
  day: number;
  hypos: GroupedHypoEvent[];
  hypers: GroupedHyperEvent[];
  hypoCount: number;
  hyperCount: number;
}

export const CalendarMonth: React.FC<{ days?: number }> = ({ days = 30 }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [daysData, setDaysData] = useState<Map<string, DayData>>(new Map());
  const [loading, setLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState<DayData | null>(null);

  useEffect(() => {
    fetchCalendarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const response = await calendarAPI.getCalendar(days);
      const dataMap = new Map<string, DayData>();

      if (Array.isArray(response.data)) {
        response.data.forEach((entry: any) => {
          if (entry.date) {
            const hypos = entry.hypos?.map((h: any) => h.hypo || h).filter((h: any) => h) || [];
            const hypers = entry.hypers || [];
            dataMap.set(entry.date, {
              date: entry.date,
              day: parseInt(entry.date.split('-')[2]),
              hypos,
              hypers,
              hypoCount: entry.hypo_count || hypos.length,
              hyperCount: entry.hyper_count || hypers.length,
            });
          }
        });
      }
      setDaysData(dataMap);
    } catch (err) {
      console.error('Calendar loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const getDateString = (year: number, month: number, day: number): string => {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const formatHypoHeader = (date: Date, startTime: string, endTime: string): string => {
    const dayOfWeek = date.toLocaleDateString('fr-FR', { weekday: 'short' });
    const dateStr = date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    return `${dayOfWeek}. ${dateStr} · ${startTime} à ${endTime}`;
  };

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const monthName = currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const calendarDays: any[] = [];

  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = getDateString(currentDate.getFullYear(), currentDate.getMonth() + 1, day);
    const dayData = daysData.get(dateStr);
    calendarDays.push({ day, dateStr, data: dayData });
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <button onClick={prevMonth} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
          ← Précédent
        </button>
        <h2 className="text-2xl font-bold text-gray-800 capitalize flex-1 text-center">{monthName}</h2>
        <button onClick={nextMonth} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
          Suivant →
        </button>
      </div>

      <div className="grid grid-cols-7 gap-2">
        {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
          <div key={day} className="text-center font-bold text-gray-600 text-sm py-2">
            {day}
          </div>
        ))}

        {calendarDays.map((dayItem, idx) => {
          const hasHypo = dayItem?.data?.hypoCount > 0;
          const hasHyper = dayItem?.data?.hyperCount > 0;
          let bgClass = 'border-gray-200 bg-white hover:bg-gray-50';

          if (hasHypo) {
            bgClass = 'border-red-300 bg-red-50 hover:bg-red-100';
          } else if (hasHyper) {
            bgClass = 'border-orange-300 bg-orange-50 hover:bg-orange-100';
          }

          return (
            <div
              key={idx}
              className={`min-h-24 p-2 rounded border cursor-pointer transition ${
                dayItem?.data ? bgClass : dayItem ? 'border-gray-200 bg-white hover:bg-gray-50' : 'bg-gray-50'
              }`}
              onClick={() => dayItem?.data && setSelectedDay(dayItem.data)}
            >
              {dayItem && (
                <div className="text-sm h-full flex flex-col">
                  <div className="font-bold text-gray-800">{dayItem.day}</div>
                  {dayItem.data && (
                    <div className="flex-1 mt-1 text-xs space-y-1">
                      {hasHypo && (
                        <div className="text-red-600 font-semibold">{dayItem.data.hypoCount} hypo</div>
                      )}
                      {hasHyper && (
                        <div className="text-orange-600 font-semibold">{dayItem.data.hyperCount} hyper</div>
                      )}
                      {dayItem.data.hypos.slice(0, 1).map((hypo: any, i: number) => (
                        <div key={`hypo-${i}`} className="bg-red-100 text-red-700 px-1 py-0.5 rounded truncate text-xs">
                          {hypo.glucose_min}-{hypo.glucose_max}
                        </div>
                      ))}
                      {dayItem.data.hypers.slice(0, 1).map((hyper: any, i: number) => (
                        <div key={`hyper-${i}`} className="bg-orange-100 text-orange-700 px-1 py-0.5 rounded truncate text-xs">
                          {hyper.glucose_min}-{hyper.glucose_max}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selectedDay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">
                {new Date(selectedDay.date + 'T00:00:00').toLocaleDateString('fr-FR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </h3>
              <button
                onClick={() => setSelectedDay(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-3">
              {selectedDay.hypos.map((hypo, idx) => {
                const startDate = hypo.detected_at ? new Date(hypo.detected_at) : null;
                const endDate = hypo.ended_at ? new Date(hypo.ended_at) : null;

                const startTime = startDate
                  ? startDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                  : '?';
                const endTime = endDate
                  ? endDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                  : '?';

                const headerText = formatHypoHeader(
                  startDate || new Date(),
                  startTime,
                  endTime
                );

                return (
                  <div key={`hypo-${hypo.id}`} className="border border-red-200 bg-red-50 rounded p-3">
                    <div className="font-semibold text-gray-800 mb-2">
                      {headerText}
                    </div>
                    <div className="text-sm text-gray-600">
                      Durée: {hypo.duration_minutes} minutes
                    </div>
                    <div className="text-sm text-red-700 font-semibold mt-2">
                      Min: {hypo.glucose_min} | Moyen: {hypo.glucose_avg.toFixed(1)} | Max: {hypo.glucose_max} mg/dL
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {hypo.readings_count} mesure{hypo.readings_count > 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}
              {selectedDay.hypers.map((hyper, idx) => {
                const startDate = hyper.detected_at ? new Date(hyper.detected_at) : null;
                const endDate = hyper.ended_at ? new Date(hyper.ended_at) : null;

                const startTime = startDate
                  ? startDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                  : '?';
                const endTime = endDate
                  ? endDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                  : '?';

                const headerText = formatHypoHeader(
                  startDate || new Date(),
                  startTime,
                  endTime
                );

                return (
                  <div key={`hyper-${hyper.id}`} className="border border-orange-200 bg-orange-50 rounded p-3">
                    <div className="font-semibold text-gray-800 mb-2">
                      {headerText}
                    </div>
                    <div className="text-sm text-gray-600">
                      Durée: {hyper.duration_minutes} minutes
                    </div>
                    <div className="text-sm text-orange-700 font-semibold mt-2">
                      Min: {hyper.glucose_min} | Moyen: {hyper.glucose_avg.toFixed(1)} | Max: {hyper.glucose_max} mg/dL
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {hyper.readings_count} mesure{hyper.readings_count > 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {loading && <div className="text-center text-gray-500 py-8">Chargement...</div>}
    </div>
  );
};
